import { PdfButton } from "../components/PdfButton";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { money, moneyCompact, num, pct } from "@owners/format";
import { api } from "../api";
import { HotelPicker, hotelParam } from "../components/Numbers";
import { LineChart, Sparkline } from "../components/LineChart";
import { MarketPanel } from "../components/MarketPanel";
import { expenses, fullYearTiles, lateCycleRows, saleTimingNote, seasons, trendsNarrative, withForecast } from "./trendsView";

/**
 * Trends (SPEC §14): up to 10 years of the hotels' own indicators (overlayable),
 * the economy panel with Watch/OK signals and public sources, and the hotel
 * cycle view (U.S. industry downturns, where we are now, our own cycle).
 */

type Key = "occupiedRooms" | "adr" | "revpar" | "totalRevenue" | "gop" | "noi";
const IND: Array<{ key: Key; label: string; color: string; fmt: (v: number) => string }> = [
  { key: "occupiedRooms", label: "Occupied rooms", color: "#15263d", fmt: (v) => num(v) },
  { key: "adr", label: "ADR", color: "#c9a45c", fmt: (v) => money(v, { decimals: 2 }) },
  { key: "revpar", label: "RevPAR", color: "#2f7d4f", fmt: (v) => money(v, { decimals: 2 }) },
  { key: "totalRevenue", label: "Total revenue", color: "#5b7db1", fmt: (v) => moneyCompact(v) },
  { key: "gop", label: "GOP", color: "#8a5a00", fmt: (v) => moneyCompact(v) },
  { key: "noi", label: "NOI", color: "#b42318", fmt: (v) => moneyCompact(v) },
];
type Vec = Array<number | null>;
interface TrendsRes {
  years: number; hotels: number; included: number; months: string[]; reporting: number[]; booksFrom: string | null;
  monthly: Record<Key | "occupancy", Vec>; annual: { years: string[]; complete: boolean[] } & Record<Key | "occupancy", Vec>;
  /** Rest of this year from Meridian's reforecast (same as Budget + Reforecast); null when not every hotel has one. */
  forecast: null | { months: string[]; totalRevenue: Vec; noi: Vec; asOf: string | null };
}
interface EconRes { indicators: Array<{ key: string; label: string; unit: string; source: string; rule: string; latest: { date: string; value: number } | null;
  signal: "watch" | "ok" | null; detail: string | null; history: Array<{ date: string; value: number }>; asOf: string | null; stale: boolean; error: string | null }> }
interface Downturn { peak: string; trough: string; recovered: string | null; depthPct: number; monthsDown: number; monthsToRecover: number | null }
interface Cycle { downturns: Downturn[]; peakToPeak: Array<{ from: string; to: string; months: number }>; valleyToValley: Array<{ from: string; to: string; months: number }>;
  now: { latest: string; monthsSinceTrough: number | null; vsPeakPct: number | null; expansionMonths: number | null; medianExpansionMonths: number | null; lateCycle: boolean | null; inDownturn: boolean };
  chart: Array<{ date: string; value: number }> }
interface CyclesRes { industry: (Cycle & { kind: "revpar" | "employment"; label: string; source: string; asOf: string | null; stale: boolean;
  /** CoStar national (V020): calendar-year RevPAR and the same in latest-year dollars. */
  annual?: Array<{ year: string; revpar: number; real: number | null }>; realBaseYear?: string | null }) | null; industryError: string | null; own: (Cycle & { measure: string }) | null }

const ECON_COLORS = ["#7c3aed", "#0e7490", "#be185d", "#4d7c0f", "#c2410c"];
const isPctUnit = (u: string) => u.startsWith("%") || u.includes("YoY") || u.includes("year over year");

/**
 * Put an economy series on the chart's x axis. Monthly: each month takes the
 * latest reading at or before it (quarterly data holds for 3 months), never
 * before the first reading. Annual: the average of the year's readings.
 */
export function econOnAxis(history: Array<{ date: string; value: number }>, axis: string[], grain: "month" | "year"): Array<number | null> {
  const obs = [...history].sort((a, b) => a.date.localeCompare(b.date));
  if (grain === "year") return axis.map((y) => { const v = obs.filter((o) => o.date.startsWith(y)).map((o) => o.value); return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null; });
  const monthsBetween = (a: string, b: string) => (Number(b.slice(0, 4)) - Number(a.slice(0, 4))) * 12 + Number(b.slice(5, 7)) - Number(a.slice(5, 7));
  return axis.map((m) => {
    let hit: { date: string; value: number } | null = null;
    for (const o of obs) { if (o.date.slice(0, 7) <= m) hit = o; else break; }
    return hit && monthsBetween(hit.date.slice(0, 7), m) < 3 ? hit.value : null;
  });
}

const mLabel = (ym: string) => new Date(ym + "-01T12:00:00Z").toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });

export function Trends() {
  const [ids, setIds] = useState<string[]>([]);
  const [years, setYears] = useState(10);
  const [grain, setGrain] = useState<"month" | "year">("year");
  const [on, setOn] = useState<Key[]>(["revpar"]);
  const [econOn, setEconOn] = useState<string[]>([]);       // economy indicators overlaid (right scale)
  const q = useQuery({ queryKey: ["trends", ids, years], queryFn: () => api<TrendsRes>(`/api/trends?years=${years}&hotelIds=${hotelParam(ids)}`) });
  const econ = useQuery({ queryKey: ["econ"], queryFn: () => api<EconRes>("/api/trends/economy"), staleTime: 3600_000 });
  const cyc = useQuery({ queryKey: ["cycles", ids], queryFn: () => api<CyclesRes>(`/api/trends/cycles?hotelIds=${hotelParam(ids)}`), staleTime: 600_000 });
  const d = q.data;

  const x = d ? (grain === "year" ? d.annual.years.map((y, i) => (d.annual.complete[i] ? y : `${y}*`)) : d.months.map((m) => m.slice(2).replace("-", "/"))) : [];
  const raw = (k: Key): Vec => (d ? (grain === "year" ? d.annual[k] : d.monthly[k]) : []);
  const indexed = on.length > 1;
  const toIndex = (v: Vec): Vec => { const b = v.find((n) => n !== null && n !== 0); return b ? v.map((n) => (n === null ? null : (n / b) * 100)) : v.map(() => null); };
  const series = IND.filter((i) => on.includes(i.key)).map((i) => ({ label: i.label, color: i.color, points: indexed ? toIndex(raw(i.key)) : raw(i.key) }));
  const single = IND.find((i) => i.key === on[0]);
  const econRight = d ? (econ.data?.indicators ?? []).flatMap((i, k) => econOn.includes(i.key)
    ? [{ label: i.label, color: ECON_COLORS[k % ECON_COLORS.length]!, points: econOnAxis(i.history, grain === "year" ? d.annual.years : d.months, grain) }] : []) : [];

  return (
    <>
      <PdfButton fileName="Trends" landscape />
      <div className="eyebrow">Owners Module · Trends</div>
      <h1>Trends</h1>
      <div className="row" style={{ marginBottom: 12 }}>
        <HotelPicker value={ids} onChange={setIds} />
        <label className="muted">Years <select value={years} onChange={(e) => setYears(Number(e.target.value))}>{[1, 2, 3, 5, 7, 10].map((n) => <option key={n} value={n}>{n}</option>)}</select></label>
        <div className="seg"><button className={grain === "year" ? "on" : ""} onClick={() => setGrain("year")}>Annual</button><button className={grain === "month" ? "on" : ""} onClick={() => setGrain("month")}>Monthly</button></div>
      </div>

      {d && <RevExpNoi d={d} grain={grain} x={x} />}

      <div className="row" style={{ alignItems: "flex-start", gap: 18 }}>
        <div className="card" style={{ flex: 3, minWidth: 420 }}>
          <h3 style={{ marginTop: 0 }}>Indicators</h3>
          <div className="row" style={{ marginBottom: 10 }}>
            {IND.map((i) => (
              <button key={i.key} className={`btn ${on.includes(i.key) ? "" : "secondary"}`} style={on.includes(i.key) ? { background: i.color, borderColor: i.color } : undefined}
                onClick={() => setOn(on.includes(i.key) ? (on.length > 1 ? on.filter((k) => k !== i.key) : on) : [...on, i.key])}>{i.label}</button>
            ))}
          </div>
          {q.isLoading ? <p className="muted">Loading…</p> : q.error ? <p className="error">{(q.error as Error).message}</p> : d && (
            <>
              <LineChart x={x} series={series} format={indexed ? (v) => num(v) : single!.fmt} tickEvery={grain === "month" ? 12 : 1}
                right={econRight.length ? { series: econRight, format: (v) => v.toFixed(1) } : undefined} />
              <p className="muted" style={{ fontSize: 12 }}>
                {indexed ? "Several indicators are overlaid as an index (first period = 100). " : ""}
                {econRight.length ? "Economy lines use the right-hand scale (their own units). " : ""}
                {grain === "year" && d.annual.complete.some((c) => !c) ? "* partial year. " : ""}
                {d.included < d.hotels ? `${num(d.hotels - d.included)} of ${num(d.hotels)} hotels have no history and are left out. ` : ""}
                A month counts only when every included hotel reported it.
                {d.booksFrom ? ` GOP and NOI start ${mLabel(d.booksFrom)}, when the books begin.` : " GOP and NOI need the monthly books, which aren't loaded for these hotels yet."}
              </p>
            </>
          )}
        </div>

        {/* Demo: the economy as a side column — click an indicator to overlay it on the chart. */}
        <aside className="econ-col" style={{ flex: 1, minWidth: 250 }}>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Economy · click to overlay</div>
          {(econ.data?.indicators ?? []).map((i, k) => {
            const sel = econOn.includes(i.key);
            return (
              <button key={i.key} type="button" className={`card econ-tile${sel ? " on" : ""}`} title={i.rule} aria-pressed={sel}
                style={sel ? { borderColor: ECON_COLORS[k % ECON_COLORS.length] } : undefined}
                onClick={() => setEconOn(sel ? econOn.filter((x2) => x2 !== i.key) : [...econOn, i.key])}>
                <div className="row" style={{ justifyContent: "space-between" }}>
                  <span className="econ-label">{sel && <span style={{ color: ECON_COLORS[k % ECON_COLORS.length] }}>● </span>}{i.label}</span>
                  {i.signal && <span className={`pill ${i.signal === "watch" ? "warn-pill" : "ok-pill"}`}>{i.signal === "watch" ? "Watch" : "OK"}</span>}
                </div>
                <div className="econ-value">{i.latest ? `${i.latest.value.toFixed(1)}${isPctUnit(i.unit) ? "%" : ""}` : "—"}
                  <span className="muted">{i.latest ? ` · ${mLabel(i.latest.date.slice(0, 7))}` : ""}</span></div>
                <Sparkline points={i.history.map((h) => h.value)} color={i.signal === "watch" ? "#8a5a00" : "#15263d"} />
                {i.detail && <div className="tile-sub">{i.detail}</div>}
                <div className="tile-sub" style={{ fontSize: 10 }}>{i.unit} · {i.source}{i.stale && i.asOf ? ` · last updated ${new Date(i.asOf).toLocaleDateString("en-US")}` : ""}</div>
                {i.error && <div className="tile-sub error">Source unavailable — will retry.</div>}
              </button>
            );
          })}
          {econ.isLoading && <div className="card muted">Loading public data…</div>}
        </aside>
      </div>

      <h2>Hotel cycles</h2>
      {cyc.isLoading ? <div className="card muted">Loading…</div> : cyc.data && (
        <div className="row" style={{ alignItems: "flex-start", gap: 18 }}>
          <div className="card" style={{ flex: 2, minWidth: 420 }}>
            <h3 style={{ marginTop: 0 }}>U.S. hotel {cyc.data.industry?.kind === "revpar" ? "RevPAR cycle" : "industry"} since 2000</h3>
            {!cyc.data.industry ? <p className="muted">Public data unavailable right now ({cyc.data.industryError}). It will retry.</p> : (
              <>
                {cyc.data.industry.annual && cyc.data.industry.annual.length > 1 && (
                  <AnnualBars rows={cyc.data.industry.annual} baseYear={cyc.data.industry.realBaseYear ?? null} downturns={cyc.data.industry.downturns} />
                )}
                <LineChart x={cyc.data.industry.chart.map((p) => p.date.slice(0, 4))} series={[{ label: cyc.data.industry.label, color: "#15263d", points: cyc.data.industry.chart.map((p) => p.value) }]}
                  format={cyc.data.industry.kind === "revpar" ? (v) => money(v, { decimals: 0 }) : (v) => `${num(v)}K`} tickEvery={36} height={220}
                  bands={cyc.data.industry.downturns.map((dt) => ({ from: dt.peak, to: dt.trough }))} />
                <p className="muted" style={{ fontSize: 12 }}>
                  {cyc.data.industry.kind === "revpar"
                    ? <>U.S. hotel RevPAR, trailing 12 months — {cyc.data.industry.source}{cyc.data.industry.asOf ? `, uploaded ${new Date(cyc.data.industry.asOf).toLocaleDateString("en-US")}` : ""}. Downturn = a fall of 5% or more. Shaded: peak to trough.</>
                    : <>Measured by {cyc.data.industry.label.toLowerCase()} (thousands of jobs), a public proxy for industry demand — {cyc.data.industry.source}. Shaded: peak to trough.</>}
                </p>
                <CycleTables c={cyc.data.industry} />
                <SaleTiming c={cyc.data.industry} />
              </>
            )}
          </div>
          <div style={{ flex: 1, minWidth: 300 }}>
            {cyc.data.industry && <NowCard title="Where we are now" c={cyc.data.industry} />}
            <LateCycle industry={cyc.data.industry?.now ?? null} own={cyc.data.own?.now ?? null} econ={econ.data?.indicators ?? []} />
            <div className="card">
              <h3 style={{ marginTop: 0 }}>Our own cycle</h3>
              {!cyc.data.own ? <p className="muted">Needs at least 2 years of history for these hotels.</p> : (
                <>
                  <LineChart x={cyc.data.own.chart.map((p) => p.date.slice(2).replace("-", "/"))} series={[{ label: "T12 RevPAR", color: "#2f7d4f", points: cyc.data.own.chart.map((p) => p.value) }]}
                    format={(v) => money(v, { decimals: 0 })} height={180} tickEvery={12} bands={cyc.data.own.downturns.map((dt) => ({ from: dt.peak, to: dt.trough }))} />
                  <p className="muted" style={{ fontSize: 12 }}>{cyc.data.own.measure}. Downturn = a fall of 5% or more.</p>
                  {cyc.data.own.downturns.map((dt) => (
                    <div key={dt.peak} style={{ fontSize: 13 }}>{mLabel(dt.peak)} → {mLabel(dt.trough)}: {pct(dt.depthPct, { signed: true })} over {num(dt.monthsDown)} months{dt.monthsToRecover !== null ? `, recovered in ${num(dt.monthsToRecover)}` : ", not yet recovered"}</div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <MarketPanel />
    </>
  );
}

function CycleTables({ c }: { c: Cycle }) {
  return (
    <>
      <table>
        <thead><tr><th>Peak</th><th>Trough</th><th className="num">Depth</th><th className="num">Months down</th><th className="num">Months to recover</th></tr></thead>
        <tbody>{c.downturns.map((d) => (
          <tr key={d.peak}><td>{mLabel(d.peak)}</td><td>{mLabel(d.trough)}</td><td className="num bad">{pct(d.depthPct, { signed: true })}</td>
            <td className="num">{num(d.monthsDown)}</td><td className="num">{d.monthsToRecover === null ? "not yet" : num(d.monthsToRecover)}</td></tr>
        ))}</tbody>
      </table>
      <div className="row" style={{ gap: 28, marginTop: 10, fontSize: 13, alignItems: "flex-start" }}>
        <div><div className="eyebrow">Peak to peak</div>{c.peakToPeak.map((p) => <div key={p.from}>{mLabel(p.from)} → {mLabel(p.to)}: {num(p.months)} months</div>)}</div>
        <div><div className="eyebrow">Valley to valley</div>{c.valleyToValley.map((p) => <div key={p.from}>{mLabel(p.from)} → {mLabel(p.to)}: {num(p.months)} months</div>)}</div>
      </div>
    </>
  );
}

function NowCard({ title, c }: { title: string; c: Cycle }) {
  const n = c.now;
  return (
    <div className="card">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h3 style={{ margin: 0 }}>{title}</h3>
        {n.inDownturn ? <span className="pill bad-pill">In a downturn</span> : n.lateCycle === null ? null
          : <span className={`pill ${n.lateCycle ? "warn-pill" : "ok-pill"}`}>{n.lateCycle ? "Late cycle" : "Mid cycle"}</span>}
      </div>
      <p style={{ fontSize: 14 }}>
        {n.inDownturn ? `Below the last peak by ${pct(Math.abs(n.vsPeakPct ?? 0))}.`
          : <>Expansion running {num(n.expansionMonths)} months since the {n.monthsSinceTrough !== null ? "last trough" : "start"}; past expansions lasted {num(n.medianExpansionMonths)} months (median). {n.vsPeakPct !== null && n.vsPeakPct < 0 ? `Currently ${pct(Math.abs(n.vsPeakPct))} below the peak.` : "At or near the peak."}</>}
      </p>
      <p className="muted" style={{ fontSize: 11 }}>Late-cycle check: the current expansion is at least as long as the median past one (proposed rule). Data through {n.latest ? mLabel(n.latest) : "—"}.</p>
    </div>
  );
}

/** Demo: revenue, expenses and NOI over time, peak/low season shading on the monthly view, full-year tiles and a plain summary. */
function RevExpNoi({ d, grain, x }: { d: TrendsRes; grain: "month" | "year"; x: string[] }) {
  const src = grain === "year" ? d.annual : d.monthly;
  const hasBooks = src.noi.some((v) => v !== null);
  // Demo: forecast for the rest of the year (dashed), from the reforecast.
  const fc = withForecast(grain, x, src.totalRevenue, src.noi, d.forecast);
  const X = fc.x;
  const series = [
    { label: "Revenue", color: "#15263d", points: fc.rev },
    ...(hasBooks ? [{ label: "Expenses", color: "#c9a45c", points: expenses(fc.rev, fc.noi) }, { label: "NOI", color: "#2f7d4f", points: fc.noi }] : []),
    ...(fc.fRev ? [{ label: "Revenue (forecast)", color: "#15263d", points: fc.fRev, dashed: true }] : []),
    ...(hasBooks && fc.fRev && fc.fNoi ? [
      { label: "Expenses (forecast)", color: "#c9a45c", points: expenses(fc.fRev, fc.fNoi), dashed: true },
      { label: "NOI (forecast)", color: "#2f7d4f", points: fc.fNoi, dashed: true }] : []),
  ];
  // Projected full year for the tiles: actual so far + reforecast (monthly or annual view alike).
  const proj = withForecast("year", d.annual.years, d.annual.totalRevenue, d.annual.noi, d.forecast, (m) => m).projected;
  const s = grain === "month" ? seasons(d.months, d.monthly.occupancy) : null;
  const bands = s ? d.months.flatMap((m, i) => {
    const k = Number(m.slice(5, 7));
    const kind = s.peak.includes(k) ? "peak" : s.low.includes(k) ? "low" : null;
    return kind ? [{ from: x[i]!, to: x[i + 1] ?? x[i]!, color: kind === "peak" ? "#c9a45c" : "#6b7280", opacity: kind === "peak" ? 0.14 : 0.08 }] : [];
  }) : [];
  const tiles = fullYearTiles(d.months, d.monthly.totalRevenue, d.monthly.noi);
  const mName = (k: number) => new Date(Date.UTC(2000, k - 1, 1)).toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
  const change = (a: number | null, b: number | null) => (a === null || !b ? null : ((a - b) / Math.abs(b)) * 100);
  const sub = (a: number | null, b: number | null) => { const c = change(a, b); return c === null ? "No prior year to compare" : `${pct(c, { signed: true })} vs prior year`; };

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Revenue, expenses and NOI</h3>
      {(tiles.lastYear || tiles.ytd) && (
        <>
          <p className="narrative" style={{ fontSize: 14 }}>{trendsNarrative(tiles, (n) => moneyCompact(n), (n) => pct(n))}</p>
          <div className="tiles strip">
            {tiles.lastYear && <>
              <div className="card tile"><div className="eyebrow">Revenue · {tiles.lastYear.label}</div><div className="tile-value">{moneyCompact(tiles.lastYear.revenue)}</div><div className="tile-sub">{sub(tiles.lastYear.revenue, tiles.lastYear.lyRevenue)}</div></div>
              {tiles.lastYear.noi !== null && <div className="card tile"><div className="eyebrow">NOI · {tiles.lastYear.label}</div><div className="tile-value">{moneyCompact(tiles.lastYear.noi)}</div><div className="tile-sub">{sub(tiles.lastYear.noi, tiles.lastYear.lyNoi)}</div></div>}
            </>}
            {tiles.ytd && <>
              <div className="card tile"><div className="eyebrow">Revenue · {tiles.ytd.label}</div><div className="tile-value">{moneyCompact(tiles.ytd.revenue)}</div><div className="tile-sub">{sub(tiles.ytd.revenue, tiles.ytd.lyRevenue)}</div></div>
              {tiles.ytd.noi !== null && <div className="card tile"><div className="eyebrow">NOI · {tiles.ytd.label}</div><div className="tile-value">{moneyCompact(tiles.ytd.noi)}</div><div className="tile-sub">{sub(tiles.ytd.noi, tiles.ytd.lyNoi)}</div></div>}
            </>}
            {proj && proj.revenue !== null && <div className="card tile"><div className="eyebrow">Revenue · {proj.year} projected</div><div className="tile-value">{moneyCompact(proj.revenue)}</div><div className="tile-sub">Actual so far + reforecast</div></div>}
            {proj && proj.noi !== null && <div className="card tile"><div className="eyebrow">NOI · {proj.year} projected</div><div className="tile-value">{moneyCompact(proj.noi)}</div><div className="tile-sub">Actual so far + reforecast</div></div>}
          </div>
        </>
      )}
      <LineChart x={X} series={series} format={(v) => moneyCompact(v)} tickEvery={grain === "month" ? 12 : 1} bands={bands} />
      <p className="muted" style={{ fontSize: 12 }}>
        Expenses are everything between revenue and NOI.
        {s ? ` Shaded: peak season (${s.peak.map(mName).join(", ")}) in gold, low season (${s.low.map(mName).join(", ")}) in grey, from average occupancy by month.` : grain === "year" ? " Switch to Monthly to see peak and low seasons." : ""}
        {!hasBooks && " NOI needs the monthly books, which aren't loaded for these hotels yet."}
        {fc.fRev ? ` Dashed: the rest of ${d.forecast!.months[0]!.slice(0, 4)} from the reforecast (same as Budget + Reforecast${d.forecast!.asOf ? `, as of ${new Date(d.forecast!.asOf).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}).`
          : d.forecast === null && d.months.at(-1)?.slice(0, 4) === String(new Date().getFullYear()) && d.months.at(-1)!.slice(5) !== "12"
            ? " No forecast line: not every hotel in view has a reforecast for this year." : ""}
      </p>
    </div>
  );
}

function LateCycle({ industry, own, econ }: { industry: Cycle["now"] | null; own: Cycle["now"] | null; econ: EconRes["indicators"] }) {
  const rows = lateCycleRows(industry, own, econ);
  if (!rows.length) return null;
  const late = rows.filter((r) => r.late === true).length, judged = rows.filter((r) => r.late !== null).length;
  return (
    <div className="card">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h3 style={{ margin: 0 }}>Late-cycle check</h3>
        <span className={`pill ${late * 2 > judged ? "warn-pill" : "ok-pill"}`}>{late} of {judged} signs</span>
      </div>
      <table style={{ marginTop: 8 }}>
        <tbody>{rows.map((r) => (
          <tr key={r.check}><td style={{ fontSize: 13 }}>{r.check}</td><td className="num" style={{ fontSize: 13 }}>{r.reading}</td>
            <td className="num">{r.late === null ? <span className="muted">—</span> : <span className={`dot dot-${r.late ? "watch" : "good"}`} title={r.late ? "Late-cycle sign" : "Not a late-cycle sign"} />}</td></tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function SaleTiming({ c }: { c: Cycle }) {
  const lines = saleTimingNote(c.downturns, c.now);
  if (!lines.length) return null;
  return (
    <div style={{ marginTop: 14 }}>
      <div className="eyebrow">What the cycles say about timing a sale</div>
      {lines.map((l, k) => <p key={k} style={{ fontSize: 14, margin: "6px 0" }}>{l}</p>)}
      <p className="muted" style={{ fontSize: 11 }}>A reading of past cycles, not a forecast or advice to buy or sell.</p>
    </div>
  );
}

/**
 * Demo: U.S. RevPAR by calendar year as bars (downturn years shaded red), with
 * the same figures in today's dollars as a line (CPI-U), so real growth shows.
 */
function AnnualBars({ rows, baseYear, downturns }: { rows: Array<{ year: string; revpar: number; real: number | null }>; baseYear: string | null; downturns: Downturn[] }) {
  const W = 900, H = 200, L = 56, R = 12, T = 12, B = 26;
  const max = Math.max(...rows.flatMap((r) => [r.revpar, r.real ?? 0])) * 1.08;
  const bw = (W - L - R) / rows.length;
  const y = (v: number) => T + (H - T - B) * (1 - v / max);
  const down = (yr: string) => downturns.some((d) => yr >= d.peak.slice(0, 4) && yr <= d.trough.slice(0, 4));
  const line = rows.map((r, i) => (r.real == null ? null : `${L + bw * i + bw / 2},${y(r.real)}`)).filter(Boolean).join(" ");
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((k) => k * max);
  return (
    <div style={{ marginBottom: 10 }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="U.S. RevPAR by year">
        {ticks.map((v, k) => <g key={k}><line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="#eef0f3" /><text x={L - 6} y={y(v) + 4} fontSize={10} textAnchor="end" fill="#6b7280">{money(v, { decimals: 0 })}</text></g>)}
        {rows.map((r, i) => (
          <g key={r.year}>
            <rect x={L + bw * i + bw * 0.15} y={y(r.revpar)} width={bw * 0.7} height={H - B - y(r.revpar)} fill={down(r.year) ? "#d98b83" : "#c9a45c"} rx={2}>
              <title>{`${r.year}: ${money(r.revpar, { decimals: 2 })}${r.real != null ? ` · ${money(r.real, { decimals: 2 })} in ${baseYear} dollars` : ""}`}</title>
            </rect>
            {(rows.length <= 14 || i % 2 === 0) && <text x={L + bw * i + bw / 2} y={H - 8} fontSize={10} textAnchor="middle" fill="#6b7280">{r.year.slice(2)}</text>}
          </g>
        ))}
        {line && <polyline points={line} fill="none" stroke="#15263d" strokeWidth={2} />}
      </svg>
      <div className="row" style={{ gap: 14, fontSize: 12 }}>
        <span><span style={{ color: "#c9a45c" }}>■</span> RevPAR by year</span>
        <span><span style={{ color: "#d98b83" }}>■</span> downturn years</span>
        {line && <span><span style={{ color: "#15263d" }}>—</span> inflation-adjusted ({baseYear} dollars, CPI-U)</span>}
      </div>
    </div>
  );
}
